import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { useDispatch } from "react-redux";
import { setFilters } from "../../store/slices/submissionFilterSlice";

const AdminDashboard = ({ dashboardData, dateRange = {} }) => {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const [selectedDistrict, setSelectedDistrict] = useState("all");

  const aggregatedData = useMemo(() => {
    const total = {
      district_name: "All Districts",
      power_loom: {
        total: 0,
        submitted_for_provisional_sanction: 0,
        field_visit_completed: 0,
        provisional_sanction_recommended: 0,
        provisional_sanction_not_recommended: 0,
        submitted_for_approval: 0,
        after_purchase_field_visit_completed: 0,
        district_officer_approve: 0,
        district_officer_reject: 0,
        committee_approve: 0,
        committee_reject: 0,
        no_of_applied: 0,
      },
      shuttle_less: {
        total: 0,
        submitted_for_provisional_sanction: 0,
        field_visit_completed: 0,
        provisional_sanction_recommended: 0,
        provisional_sanction_not_recommended: 0,
        submitted_for_approval: 0,
        after_purchase_field_visit_completed: 0,
        district_officer_approve: 0,
        district_officer_reject: 0,
        committee_approve: 0,
        committee_reject: 0,
        no_of_applied: 0,
      },
      cfc: {
        total: 0,
        submitted_for_provisional_sanction: 0,
        field_visit_completed: 0,
        provisional_sanction_recommended: 0,
        provisional_sanction_not_recommended: 0,
        submitted_for_approval: 0,
        after_purchase_field_visit_completed: 0,
        district_officer_approve: 0,
        district_officer_reject: 0,
        committee_approve: 0,
        committee_reject: 0,
        no_of_applied: 0,
      },
      air_jet: {
        total: 0,
        submitted_for_provisional_sanction: 0,
        field_visit_completed: 0,
        provisional_sanction_recommended: 0,
        provisional_sanction_not_recommended: 0,
        submitted_for_approval: 0,
        after_purchase_field_visit_completed: 0,
        district_officer_approve: 0,
        district_officer_reject: 0,
        committee_approve: 0,
        committee_reject: 0,
        no_of_applied: 0,
      },
    };

    dashboardData?.data?.forEach((d) => {
      ["power_loom", "shuttle_less", "cfc", "air_jet"].forEach((key) => {
        Object.keys(total[key]).forEach((field) => {
          total[key][field] += d[key][field] || 0;
        });
      });
    });

    return total;
  }, [dashboardData]);

  const applicationData = dashboardData?.application_details || {
    power_loom: { total: 0, applied: 0, available: 0 },
    shuttle_less: { total: 0, applied: 0, available: 0 },
    cfc: { total: 0, applied: 0, available: 0 },
  };

  const currentData =
    selectedDistrict === "all"
      ? aggregatedData
      : dashboardData.data?.find(
          (d) => d.district_id === Number(selectedDistrict),
        );

  const handleCardClick = (mainTab, statusTab) => {
    dispatch(
      setFilters({
        mainTab: mainTab,
        statusTab: statusTab,
        eligibility: "all",
        districts: selectedDistrict === "all" ? [] : [Number(selectedDistrict)],
        fromDate: dateRange.from,
        toDate: dateRange.to,
      }),
    );
    nav("/submissions");
  };

  const renderStats = (category) => {
    const categoryMap = {
      power_loom: "modernization",
      shuttle_less: "new_shuttleless",
      cfc: "cfc_subsidy",
      air_jet: "air_jet_loom",
    };

    const stats = [
      {
        title: "Total Applications",
        value: currentData[category].total,
        color: "text-blue-600",
        status: "all",
      },
      {
        title: "Application Submitted for Provisional Sanction",
        value: currentData[category].submitted_for_provisional_sanction,
        color: "text-purple-600",
        status: "submitted_for_provisional_sanction",
      },
      {
        title: "Field Visit Completed",
        value: currentData[category].field_visit_completed,
        color: "text-indigo-600",
        status: "field_visit_completed",
      },
      {
        title: "Provisional Sanction Recommended",
        value: currentData[category].provisional_sanction_recommended,
        color: "text-green-600",
        status: "provisional_sanction_recommended",
      },
      {
        title: "Provisional Sanction Not Recommended",
        value: currentData[category].provisional_sanction_not_recommended,
        color: "text-red-600",
        status: "provisional_sanction_not_recommended",
      },
      {
        title: "Final proposal submitted",
        value: currentData[category].submitted_for_approval,
        color: "text-teal-600",
        status: "submitted_for_approval",
      },
      {
        title: "Second Field Visit",
        value: currentData[category].after_purchase_field_visit_completed,
        color: "text-orange-600",
        status: "after_purchase_field_visit_completed",
      },
      {
        title: "Deputy Director / Assistant Director Approved",
        value: currentData[category].district_officer_approve,
        color: "text-green-700",
        status: "district_officer_approve",
      },
      {
        title: "Deputy Director / Assistant Director Rejected",
        value: currentData[category].district_officer_reject,
        color: "text-red-700",
        status: "district_officer_reject",
      },
      {
        title: "Total No. of Application Approved by Committee",
        value: currentData[category].committee_approve,
        color: "text-emerald-600",
        status: "committee_approve",
      },
      {
        title: "Total No. of Application Rejected by Committee",
        value: currentData[category].committee_reject,
        color: "text-rose-600",
        status: "committee_reject",
      },
      ...(category != "cfc"
        ? [
            {
              title: "Total No. of Looms Applied",
              value: currentData[category].no_of_applied,
              color: "text-green-800",
              status: "all",
            },
          ]
        : []),
      ...(category == "cfc"
        ? [
            {
              title: "Total No. of CFC Applied",
              value: currentData[category].no_of_applied,
              color: "text-green-800",
              status: "all",
            },
          ]
        : []),
    ];

    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        {stats.map((stat, index) => (
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            key={index}
            onClick={() => handleCardClick(categoryMap[category], stat.status)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 h-[60px]">
              <CardTitle className="text-xs font-medium">
                {stat.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${stat.color} mt-4`}>
                {stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const applicationDetails = (category) => {
    const stats = [
      {
        title: "Total No. of Loom Allocated",
        value: applicationData[category].total,
        color: "text-blue-600",
      },
      {
        title: "No. of Loom Applied for Subsidy",
        value: applicationData[category].applied,
        color: "text-orange-600",
      },
      {
        title: "No. of Loom Available for Subsidy",
        value: applicationData[category].available,
        color: "text-purple-600",
      },
    ];

    return (
      <Card className="cursor-pointer hover:shadow-md transition-shadow">
        <CardContent className="w-full mt-5">
          {stats.map((stat, index) => (
            <div
              className={`text-md flex justify-between items-center font-bold ${stat.color}`}
              key={index}
            >
              <p>{stat.title}</p> <p> {stat.value}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  const cfcApplicationDetails = (category) => {
    const stats = [
      {
        title: "Total No. of CFC Allocated",
        value: applicationData[category].total,
        color: "text-blue-600",
      },
      {
        title: "No. of CFC Applied for Subsidy",
        value: applicationData[category].applied,
        color: "text-orange-600",
      },
      {
        title: "No. of CFC Available for Subsidy",
        value: applicationData[category].available,
        color: "text-purple-600",
      },
    ];

    return (
      <Card className="cursor-pointer hover:shadow-md transition-shadow">
        <CardContent className="w-full mt-5">
          {stats.map((stat, index) => (
            <div
              className={`text-md flex justify-between items-center font-bold ${stat.color}`}
              key={index}
            >
              <p>{stat.title}</p> <p> {stat.value}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      <div>
        <h3 className="text-lg font-bold">Application Details</h3>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <h4 className="text-md font-semibold pb-2 h-[60px]">
            Upgradation of 3 year Old Conventional Powerloom into Rapier
            Shuttleless Loom
          </h4>
          {applicationDetails("power_loom")}
        </div>
        <div className="flex-1">
          <h4 className="text-md font-semibold pb-2 h-[60px]">
            Purchase of New Rapier Loom / Replacement of existing Low Speed
            Rapier Looms
          </h4>
          {applicationDetails("shuttle_less")}
        </div>
        <div className="flex-1">
          <h4 className="text-md font-semibold pb-2 h-[60px]">
            Common Facility Centre
          </h4>
          {cfcApplicationDetails("cfc")}
        </div>
      </div>

      <Separator className="my-4" />

      <div className="flex justify-between">
        <div>
          <h3 className="text-lg font-bold">Scheme wise Details</h3>
        </div>
        <div className="w-60">
          <Select
            value={selectedDistrict}
            onValueChange={(value) => setSelectedDistrict(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select District" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Districts</SelectItem>
              {dashboardData?.data?.map((d) => (
                <SelectItem
                  key={d.district_id}
                  value={d.district_id.toString()}
                >
                  {d.district_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="">
        <h4 className="text-xl font-semibold pb-2">
          Upgradation of 3 year Old Conventional Powerloom into Rapier
          Shuttleless Loom
        </h4>
        {renderStats("power_loom")}
      </div>

      <Separator />

      <div className="">
        <h4 className="text-xl font-semibold pb-2">
          Purchase of New Rapier Loom / Replacement of existing Low Speed Rapier
          Looms
        </h4>
        {renderStats("shuttle_less")}
      </div>

      <Separator />

      <div className="">
        <h4 className="text-xl font-semibold pb-2">Common Facility Centre</h4>
        {renderStats("cfc")}
      </div>

      {/* <div className="">
        <h4 className="text-xl font-semibold pb-2">
          Purchase of Brand New Air Jet Looms
        </h4>
        {renderStats("air_jet")}
      </div> */}
    </div>
  );
};

export default AdminDashboard;
